import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiError, downloadPdf } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Check, X, Download, CalendarDays, FileDown } from "lucide-react";
import { API, tokenStore } from "@/lib/api";
import { toast } from "sonner";

const TYPE_LABEL = { conges_payes: "Congés payés", rtt: "RTT", maladie: "Maladie" };

function currentMonthISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function RHLeaves() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("en_attente");
  const [action, setAction] = useState(null);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);
  const [exportMonth, setExportMonth] = useState(currentMonthISO());
  const location = useLocation();
  const navigate = useNavigate();

  const load = async () => {
    try { const { data } = await api.get("/leaves"); setItems(data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  // Deep-link from push notification: /rh/conges?approve=<id> auto-opens the approve dialog
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const approveId = params.get("approve");
    const focusId = params.get("focus");
    const target = approveId || focusId;
    if (!target || !items.length) return;
    const leave = items.find((l) => l.id === target);
    if (!leave) return;
    if (approveId && leave.status === "en_attente") {
      setAction({ leave, status: "approuve" });
    }
    navigate("/rh/conges", { replace: true });
  }, [location.search, items, navigate]);

  const confirm = async () => {
    setSaving(true);
    try {
      await api.patch(`/leaves/${action.leave.id}`, { status: action.status, commentaire_rh: comment });
      toast.success(action.status === "approuve" ? "Demande approuvée" : "Demande rejetée");
      setAction(null); setComment("");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const dl = async (id) => {
    try { await downloadPdf(`/leaves/${id}/pdf`, `lettre_${id.slice(0,8)}.pdf`); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const exportCsv = async () => {
    const token = tokenStore.get();
    const url = new URL(`${API}/leaves/export.csv`);
    if (exportMonth) url.searchParams.set("month", exportMonth);
    try {
      const res = await fetch(url.toString(), { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `conges_${exportMonth || "tous"}.csv`;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(link.href), 1000);
      toast.success(`Export ${exportMonth || "complet"} téléchargé`);
    } catch (err) {
      toast.error(`Export échoué : ${err.message}`);
    }
  };

  const filtered = items.filter((i) => tab === "tous" ? true : i.status === tab);

  return (
    <div>
      <PageHeader
        title="Demandes de congé"
        subtitle="Approuvez ou rejetez les demandes soumises par les employés."
        action={
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <input
              type="month" value={exportMonth} onChange={(e) => setExportMonth(e.target.value)}
              data-testid="export-month-input"
              className="h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
            />
            <Button
              variant="outline" onClick={exportCsv} data-testid="export-csv-button"
              className="border-[#0066FF]/30 text-[#0066FF] hover:bg-[#0066FF]/5"
            >
              <FileDown className="h-4 w-4 mr-1.5" />Export paie CSV
            </Button>
          </div>
        }
      />

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList data-testid="leaves-tabs">
          <TabsTrigger value="en_attente" data-testid="tab-en-attente">En attente ({items.filter(i=>i.status==="en_attente").length})</TabsTrigger>
          <TabsTrigger value="approuve">Approuvés</TabsTrigger>
          <TabsTrigger value="rejete">Rejetés</TabsTrigger>
          <TabsTrigger value="tous">Tous</TabsTrigger>
        </TabsList>
        <TabsContent value={tab} className="mt-4">
          <Card className="border-slate-200 overflow-hidden">
            {filtered.length === 0 ? (
              <div className="p-12 text-center">
                <CalendarDays className="h-10 w-10 text-slate-300 mx-auto" />
                <p className="mt-3 text-sm text-slate-500">Aucune demande</p>
              </div>
            ) : (
              <>
              <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Employé</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Période</TableHead>
                    <TableHead>Jours ouvrés</TableHead>
                    <TableHead>Motif</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((l) => (
                    <TableRow key={l.id} data-testid={`rh-leave-row-${l.id}`}>
                      <TableCell>
                        <div className="font-medium">{l.user_name}</div>
                        <div className="text-xs text-slate-500">{l.user_email}</div>
                      </TableCell>
                      <TableCell>{TYPE_LABEL[l.type_conge] || l.type_conge}</TableCell>
                      <TableCell className="text-sm">{l.date_debut} → {l.date_fin}</TableCell>
                      <TableCell>{l.nb_jours}</TableCell>
                      <TableCell className="max-w-xs truncate text-sm text-slate-600">{l.motif}</TableCell>
                      <TableCell><StatusBadge status={l.status} /></TableCell>
                      <TableCell className="text-right">
                        {l.status === "en_attente" ? (
                          <div className="flex gap-2 justify-end">
                            <Button data-testid={`approve-leave-${l.id}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setAction({ leave: l, status: "approuve" })}><Check className="h-3.5 w-3.5 mr-1" />Approuver</Button>
                            <Button data-testid={`reject-leave-${l.id}`} size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setAction({ leave: l, status: "rejete" })}><X className="h-3.5 w-3.5 mr-1" />Rejeter</Button>
                          </div>
                        ) : l.status === "approuve" ? (
                          <Button size="sm" variant="outline" onClick={() => dl(l.id)}><Download className="h-3.5 w-3.5 mr-1" />PDF</Button>
                        ) : <span className="text-xs text-slate-400">—</span>}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
              <div className="md:hidden divide-y divide-slate-100">
                {filtered.map((l) => (
                  <div key={l.id} className="p-4" data-testid={`rh-leave-card-${l.id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate">{l.user_name}</div>
                        <div className="text-xs text-slate-500 truncate">{l.user_email}</div>
                      </div>
                      <StatusBadge status={l.status} />
                    </div>
                    <div className="text-xs text-slate-600 mb-1"><b>{TYPE_LABEL[l.type_conge] || l.type_conge}</b> · {l.date_debut} → {l.date_fin} · <b>{l.nb_jours}</b> j</div>
                    {l.motif && <p className="text-xs text-slate-500 line-clamp-2 mb-3">{l.motif}</p>}
                    {l.status === "en_attente" ? (
                      <div className="grid grid-cols-2 gap-2">
                        <Button data-testid={`approve-leave-m-${l.id}`} size="sm" className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setAction({ leave: l, status: "approuve" })}><Check className="h-3.5 w-3.5 mr-1" />Approuver</Button>
                        <Button data-testid={`reject-leave-m-${l.id}`} size="sm" variant="outline" className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => setAction({ leave: l, status: "rejete" })}><X className="h-3.5 w-3.5 mr-1" />Rejeter</Button>
                      </div>
                    ) : l.status === "approuve" ? (
                      <Button size="sm" variant="outline" className="w-full" onClick={() => dl(l.id)}><Download className="h-3.5 w-3.5 mr-1" />Télécharger la lettre PDF</Button>
                    ) : null}
                  </div>
                ))}
              </div>
              </>
            )}
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={!!action} onOpenChange={(o) => { if (!o) { setAction(null); setComment(""); } }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{action?.status === "approuve" ? "Approuver la demande" : "Rejeter la demande"}</DialogTitle></DialogHeader>
          <div className="text-sm text-slate-600">
            Employé : <span className="font-medium text-slate-900">{action?.leave?.user_name}</span><br />
            Période : {action?.leave?.date_debut} → {action?.leave?.date_fin} ({action?.leave?.nb_jours} j)
          </div>
          <Textarea data-testid="rh-comment-textarea" placeholder="Commentaire (facultatif)" value={comment} onChange={(e) => setComment(e.target.value)} rows={3} />
          <DialogFooter>
            <Button variant="outline" onClick={() => { setAction(null); setComment(""); }}>Annuler</Button>
            <Button data-testid="confirm-leave-action" onClick={confirm} disabled={saving} className={action?.status === "approuve" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}>
              {saving ? "…" : "Confirmer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
